// src/firebase/canvas.js
// Real-time canvas (RTDB for low latency)
import {
  ref, set, get, onValue, push, remove, query, orderByKey, limitToLast
} from 'firebase/database';
import { rtdb } from './index';

export const pushStroke = async (roomId, stroke) => {
  const strokesRef = ref(rtdb, `canvas/${roomId}/strokes`);
  await push(strokesRef, stroke);
};

export const undoLastStroke = async (roomId) => {
  // Push keys are chronological, so only the newest key needs to be fetched.
  const lastStrokeQuery = query(
    ref(rtdb, `canvas/${roomId}/strokes`),
    orderByKey(),
    limitToLast(1)
  );
  const snapshot = await get(lastStrokeQuery);
  let lastStrokeKey = null;
  snapshot.forEach(strokeSnapshot => {
    lastStrokeKey = strokeSnapshot.key;
  });

  if (!lastStrokeKey) return false;
  await remove(ref(rtdb, `canvas/${roomId}/strokes/${lastStrokeKey}`));
  return true;
};

// Live stroke: the in-progress stroke from the current drawer (overwritten on each move)
export const setLiveStroke = (roomId, stroke) => {
  const liveRef = ref(rtdb, `canvas/${roomId}/live`);
  set(liveRef, stroke); // fire-and-forget, no await needed for low-latency feel
};

export const clearLiveStroke = async (roomId) => {
  await set(ref(rtdb, `canvas/${roomId}/live`), null);
};

export const clearCanvas = async (roomId) => {
  const canvasRef = ref(rtdb, `canvas/${roomId}`);
  await set(canvasRef, { cleared: Date.now() });
};

export const listenCanvas = (roomId, onStrokes, onClear, onLive) => {
  const strokesRef = ref(rtdb, `canvas/${roomId}/strokes`);
  const clearedRef = ref(rtdb, `canvas/${roomId}/cleared`);
  const liveRef    = ref(rtdb, `canvas/${roomId}/live`);

  // Detach only these callbacks; off(ref) would also remove other listeners on the path.
  const unsubStrokes = onValue(strokesRef, (snap) => {
    const val = snap.val();
    onStrokes(val ? Object.values(val) : []);
  });

  const unsubCleared = onValue(clearedRef, (snap) => {
    if (snap.val()) onClear();
  });

  const unsubLive = onLive
    ? onValue(liveRef, (snap) => {
      onLive(snap.val() || null);
    })
    : null;

  return () => {
    unsubStrokes();
    unsubCleared();
    if (unsubLive) unsubLive();
  };
};
